import { useEffect, useMemo, useState } from "react";
import type {
  BuyerAllocationValuesDto,
  ResolvedBuyerAllocation,
  SaveBuyerAllocationPayload,
} from "@/api/buyers";
import { Modal } from "@/components/ui/Modal";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";

/**
 * The editor for ONE layer of the allocation policy — the global row, or one
 * specialist's override.
 *
 * <p>🔴 <b>Every field here is tri-state and the UI has to say which state it is
 * in, because the API cannot infer it.</b> `null` means INHERIT from the layer
 * beneath; a number means override with that number. An empty text box is
 * therefore not a value — it is the absence of one — and sending `0` for a
 * cleared field would silently rewrite the policy instead of deferring to it.
 * Each numeric field is rendered as an explicit "override this" checkbox plus a
 * number, so a blank box can never reach the server carrying meaning.</p>
 *
 * <p><b>The cap is tri-state twice over.</b> `maxActiveBuyers` distinguishes
 * inherit (null) from explicitly-uncapped (non-positive) from a real ceiling
 * (positive) — "no cap" is a decision a layer can make, and it is not the same
 * as declining to decide. So it gets a second checkbox rather than relying on a
 * blank or a zero to carry it.</p>
 */
export interface BuyerAllocationEditorModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** What "inherit" resolves to for this layer, shown beside each blank field. */
  inherited: ResolvedBuyerAllocation;
  /** Word for the layer beneath, e.g. "the shipped defaults" or "the global policy". */
  inheritedLabel: string;
  /** Current authored values. Undefined for a brand-new override. */
  values?: BuyerAllocationValuesDto;
  /** Rendered above the form when the caller has a specialist to name. */
  subject?: string;
  saving?: boolean;
  /** Server-side rejection text. Surfaced verbatim — the server owns the rules. */
  error?: string | null;
  onSave: (payload: SaveBuyerAllocationPayload) => void;
}

/** Empty string is "the box is blank", which is only ever reachable while the
 *  matching override checkbox is on — a blank box is a validation error, never
 *  a value the save path has to interpret. */
type Draft = {
  overrideStarting: boolean;
  starting: string;
  overrideThreshold: boolean;
  threshold: string;
  overrideCap: boolean;
  capEnforced: boolean;
  cap: string;
  notes: string;
};

function draftFrom(values: BuyerAllocationValuesDto | undefined): Draft {
  const cap = values?.maxActiveBuyers;
  return {
    overrideStarting: values?.startingAllocation != null,
    starting: values?.startingAllocation?.toString() ?? "",
    overrideThreshold: values?.qualifyingThreshold != null,
    threshold: values?.qualifyingThreshold?.toString() ?? "",
    overrideCap: cap != null,
    // A stored non-positive cap is the "explicitly uncapped" state, so it turns
    // the enforce switch OFF rather than landing in the number box as a 0.
    capEnforced: cap != null && cap > 0,
    cap: cap != null && cap > 0 ? cap.toString() : "",
    notes: values?.notes ?? "",
  };
}

export function BuyerAllocationEditorModal({
  open,
  onClose,
  title,
  inherited,
  inheritedLabel,
  values,
  subject,
  saving = false,
  error,
  onSave,
}: BuyerAllocationEditorModalProps) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(values));

  // Re-seed whenever the modal is (re)opened onto a different subject, so the
  // previous row's numbers never bleed into the next one.
  useEffect(() => {
    if (open) setDraft(draftFrom(values));
  }, [open, values]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const startingError = fieldError(draft.overrideStarting, draft.starting, 0);
  const thresholdError = fieldError(draft.overrideThreshold, draft.threshold, 1);
  const capError =
    draft.overrideCap && draft.capEnforced ? fieldError(true, draft.cap, 1) : null;

  const invalid = Boolean(startingError || thresholdError || capError);

  const payload = useMemo<SaveBuyerAllocationPayload>(
    () => ({
      startingAllocation: draft.overrideStarting ? Number(draft.starting) : null,
      qualifyingThreshold: draft.overrideThreshold ? Number(draft.threshold) : null,
      // Off entirely → null (inherit). On but not enforcing → 0, the server's
      // spelling of "no ceiling applies at this layer".
      maxActiveBuyers: !draft.overrideCap ? null : draft.capEnforced ? Number(draft.cap) : 0,
      notes: draft.notes.trim() === "" ? null : draft.notes.trim(),
    }),
    [draft],
  );

  const clearsEverything =
    payload.startingAllocation == null &&
    payload.qualifyingThreshold == null &&
    payload.maxActiveBuyers == null &&
    payload.notes == null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={`Anything left on "inherit" follows ${inheritedLabel}.`}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => onSave(payload)} disabled={saving || invalid}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-4">
        {subject && <p className="text-small text-onyx">{subject}</p>}

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}

        <NumberOverride
          label="Starting allocation"
          helper="Buyers a consultant may hold before earning any."
          inheritedValue={inherited.startingAllocation}
          inheritedLabel={inheritedLabel}
          enabled={draft.overrideStarting}
          onToggle={(v) => set("overrideStarting", v)}
          value={draft.starting}
          onChange={(v) => set("starting", v)}
          error={startingError}
          min={0}
        />

        <NumberOverride
          label="Qualifying threshold"
          helper="Qualifying submissions per additional earned buyer."
          inheritedValue={inherited.qualifyingThreshold}
          inheritedLabel={inheritedLabel}
          enabled={draft.overrideThreshold}
          onToggle={(v) => set("overrideThreshold", v)}
          value={draft.threshold}
          onChange={(v) => set("threshold", v)}
          error={thresholdError}
          min={1}
        />

        <fieldset className="border border-border-light rounded-md p-space-3">
          <legend className="text-label uppercase tracking-wide text-gray-mid px-space-1">
            Cap on active buyers
          </legend>

          <label className="flex items-center gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={draft.overrideCap}
              onChange={(e) => set("overrideCap", e.target.checked)}
            />
            Set the cap here
          </label>
          {!draft.overrideCap && (
            <p className="text-caption text-gray-mid mt-space-1">
              Inheriting {inheritedLabel} — currently{" "}
              {inherited.maxActiveBuyers == null ? "no cap" : inherited.maxActiveBuyers}.
            </p>
          )}

          {draft.overrideCap && (
            <div className="mt-space-3 flex flex-col gap-space-2">
              <label className="flex items-center gap-space-2 text-small text-onyx">
                <input
                  type="checkbox"
                  checked={draft.capEnforced}
                  onChange={(e) => set("capEnforced", e.target.checked)}
                />
                Enforce a ceiling
              </label>
              {draft.capEnforced ? (
                <FormField label="Maximum active buyers" error={capError ?? undefined}>
                  <Input
                    type="number"
                    min={1}
                    value={draft.cap}
                    onChange={(e) => set("cap", e.target.value)}
                  />
                </FormField>
              ) : (
                <p className="text-caption text-gray-mid">
                  No ceiling. This is a decision recorded at this layer, not the
                  same as inheriting one.
                </p>
              )}
            </div>
          )}
        </fieldset>

        <FormField label="Notes" helperText="Why this policy is set the way it is.">
          <Textarea
            rows={3}
            value={draft.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </FormField>

        {clearsEverything && (
          <p className="text-caption text-gray-mid">
            Everything is on inherit, so saving clears this row entirely and
            reverts to {inheritedLabel}.
          </p>
        )}
      </div>
    </Modal>
  );
}

function NumberOverride({
  label,
  helper,
  inheritedValue,
  inheritedLabel,
  enabled,
  onToggle,
  value,
  onChange,
  error,
  min,
}: {
  label: string;
  helper: string;
  inheritedValue: number;
  inheritedLabel: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  min: number;
}) {
  return (
    <fieldset className="border border-border-light rounded-md p-space-3">
      <legend className="text-label uppercase tracking-wide text-gray-mid px-space-1">
        {label}
      </legend>
      <p className="text-caption text-gray-mid mb-space-2">{helper}</p>

      <label className="flex items-center gap-space-2 text-small text-onyx">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
        />
        Set {label.toLowerCase()} here
      </label>

      {enabled ? (
        <div className="mt-space-2">
          <FormField label={label} error={error ?? undefined}>
            <Input
              type="number"
              min={min}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          </FormField>
        </div>
      ) : (
        <p className="text-caption text-gray-mid mt-space-1">
          Inheriting {inheritedLabel} — currently {inheritedValue}.
        </p>
      )}
    </fieldset>
  );
}

/**
 * Only the two conditions a blank/garbled box creates. The real policy rules
 * (the threshold's ">= 1", the starting allocation's ">= 0") live on the server
 * and their messages are surfaced verbatim — this is here so the request is
 * well-formed, not to duplicate the server's copy.
 */
function fieldError(enabled: boolean, raw: string, min: number): string | null {
  if (!enabled) return null;
  if (raw.trim() === "") return "Enter a number, or turn this off to inherit.";
  const n = Number(raw);
  if (!Number.isInteger(n)) return "Enter a whole number.";
  if (n < min) return `Must be ${min} or more.`;
  return null;
}
